// AiSpendView — controle de gastos mensais com a IA da plataforma.
//
// O aluno define:
//   - Teto do mês (em R$). Quando atinge o limite, novas chamadas que
//     consomem a chave da plataforma são bloqueadas até o próximo ciclo.
//   - Threshold de alerta (em %). Quando o consumo passa daí no mês,
//     a plataforma marca um aviso (e o auto-debit cron pausa cobranças
//     automáticas até o próximo ciclo).
//
// Chamadas BYOK não contam — o aluno paga direto ao provider.
// [[project-ai-spend-controls]]

import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Key,
  Loader2,
  Save,
  Settings2,
  Sparkles,
  Wallet
} from "lucide-react";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { Slider } from "@app/ui/components/slider";
import {
  uiPrefs,
  setAiAccessMode,
  setAiProvider,
  type AiProvider
} from "./uiPrefs";
import { setActiveNav } from "./App";

interface SpendSettings {
  maxMonthCents: number | null;
  warnPct: number;
  lastWarnedAtIso: string | null;
  autoDebitPausedUntilIso: string | null;
}

interface SpendResponse {
  settings: SpendSettings;
  defaults: SpendSettings;
  monthToDateCents: number;
  pctOfLimit: number | null;
  blocked: boolean;
  periodStartIso: string;
  periodEndIso: string;
}

// O preço da plataforma é em centavos de USD; mostramos em R$ pra facilitar a
// vida do aluno. Usamos uma taxa fixa indicativa — não é cobrança real, só
// referência visual. Ajustar quando a plataforma adotar BRL nativo.
const USD_TO_BRL = 5.0;

function centsUsdToReais(cents: number): string {
  const brl = (cents / 100) * USD_TO_BRL;
  return brl.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

function brlToCentsUsd(brl: number): number {
  return Math.round((brl / USD_TO_BRL) * 100);
}

function centsUsdToBrlNumber(cents: number): number {
  return Number(((cents / 100) * USD_TO_BRL).toFixed(2));
}

export function AiSpendView({ embedded = false }: { embedded?: boolean } = {}) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [data, setData] = useState<SpendResponse | null>(null);
  const [draftBrl, setDraftBrl] = useState<string>("");
  const [draftWarn, setDraftWarn] = useState<number>(80);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await call<SpendResponse>("/api/student/ai-spend");
      setData(r);
      setDraftBrl(
        r.settings.maxMonthCents
          ? String(centsUsdToBrlNumber(r.settings.maxMonthCents))
          : ""
      );
      setDraftWarn(r.settings.warnPct);
      setPhase("ready");
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const brl = Number(draftBrl.replace(",", "."));
      const maxMonthCents =
        !draftBrl || !Number.isFinite(brl) || brl <= 0 ? null : brlToCentsUsd(brl);
      await call("/api/student/ai-spend", {
        method: "PUT",
        body: JSON.stringify({
          maxMonthCents,
          warnPct: draftWarn
        })
      });
      setSavedAt(Date.now());
      await load();
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  if (phase === "loading") {
    return (
      <div class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </div>
    );
  }
  if (phase === "error" || !data) {
    return (
      <div class="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {errMsg ?? "Falha ao carregar."}
      </div>
    );
  }

  const pct = data.pctOfLimit ?? 0;
  const pctClamped = Math.min(100, pct);
  const overWarn = data.settings.maxMonthCents
    ? pct >= data.settings.warnPct
    : false;
  const periodEnd = new Date(data.periodEndIso).toLocaleDateString("pt-BR");

  return (
    <div class="space-y-5">
      {!embedded && (
        <header>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Wallet className="size-6 text-primary" />
            Controle de gastos com IA
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Defina um teto mensal para o uso da IA da plataforma. Chamadas com a
            sua própria chave (BYOK) não entram nesta conta — o gasto delas vai
            direto na sua fatura com o provedor.
          </p>
        </header>
      )}

      {/* Status do mês corrente */}
      <section class="space-y-3 rounded-md border border-border bg-card p-5">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-sm font-bold">Gasto neste ciclo</h2>
          <span class="text-[11px] text-muted-foreground">
            até {periodEnd}
          </span>
        </div>
        <div class="flex flex-wrap items-baseline justify-between gap-3">
          <div class="flex items-baseline gap-3">
            <span class="text-3xl font-bold tabular-nums text-primary">
              {centsUsdToReais(data.monthToDateCents)}
            </span>
            {data.settings.maxMonthCents && (
              <span class="text-sm text-muted-foreground">
                de {centsUsdToReais(data.settings.maxMonthCents)}
              </span>
            )}
          </div>
          {/* % consumido em destaque ao lado do valor — cor muda com o estado
              (vermelho se bloqueado, âmbar se passou do warn, primary normal).
              Só faz sentido se tem teto configurado. */}
          {data.settings.maxMonthCents && (
            <span
              class={`text-2xl font-bold tabular-nums ${
                data.blocked
                  ? "text-destructive"
                  : overWarn
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-primary"
              }`}
              title="% do teto mensal já consumido"
            >
              {pct.toFixed(1)}%
            </span>
          )}
        </div>
        {data.settings.maxMonthCents && (
          <div class="h-2 overflow-hidden rounded-full bg-muted">
            <div
              class={`h-full rounded-full transition-all ${
                data.blocked
                  ? "bg-destructive"
                  : overWarn
                    ? "bg-amber-500"
                    : "bg-primary"
              }`}
              style={{ width: `${pctClamped}%` }}
            />
          </div>
        )}
        {!data.settings.maxMonthCents && (
          <div class="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-[12px] text-muted-foreground">
            <AlertCircle className="mr-1 inline size-3.5 align-text-bottom" />
            <strong class="text-foreground">Configure um teto mensal</strong>{" "}
            abaixo para acompanhar quanto % já foi consumido neste ciclo. Sem
            teto, todas as chamadas usando a chave da plataforma ficam liberadas
            (sem % de referência).
          </div>
        )}

        {data.blocked && (
          <div class="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <div>
              <strong>Limite atingido.</strong> Novas chamadas usando a chave
              da plataforma estão pausadas até o próximo ciclo ({periodEnd}).
              Cobranças automáticas também ficam suspensas — você ainda pode
              gerar pagamento manual ou aumentar o teto para liberar.
            </div>
          </div>
        )}

        {!data.blocked && overWarn && (
          <div class="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            Você já passou de {data.settings.warnPct}% do seu teto. Considere
            ajustar o limite ou reduzir o uso até o fim do ciclo.
          </div>
        )}
      </section>

      {/* Configuração */}
      <section class="space-y-4 rounded-md border border-border bg-card p-5">
        <header>
          <h2 class="text-sm font-bold">Configuração</h2>
          <p class="mt-0.5 text-[12px] text-muted-foreground">
            O ciclo é mensal (UTC). Quando você muda o teto, o aviso e a pausa
            de cobrança automática são resetados automaticamente.
          </p>
        </header>

        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Teto do mês (R$) — vazio = sem limite
          </span>
          <input
            type="number"
            inputMode="decimal"
            step="1"
            min="0"
            value={draftBrl}
            onInput={(e) => setDraftBrl((e.target as HTMLInputElement).value)}
            placeholder="Ex: 50"
            class="w-full max-w-xs rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <p class="mt-1 text-[11px] text-muted-foreground">
            Conversão indicativa USD→BRL fixa em R$ {USD_TO_BRL.toFixed(2)}.
            O cálculo real é em USD (centavos) — a barra acima reflete o valor
            ajustado.
          </p>
        </label>

        <div>
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Avisar ao atingir (%)
          </span>
          {/* Input numérico ANTES da barra: aluno consegue digitar o valor
              exato (ex: 73%) sem precisar arrastar com precisão. A barra
              fica ao lado pra ajuste rápido visual. */}
          <div class="flex items-center gap-3">
            <div class="flex items-center gap-1.5 shrink-0">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={100}
                step={1}
                value={draftWarn}
                onInput={(e) => {
                  const raw = Number((e.target as HTMLInputElement).value);
                  if (!Number.isFinite(raw)) return;
                  setDraftWarn(Math.min(100, Math.max(1, Math.round(raw))));
                }}
                aria-label="Porcentagem de alerta"
                class="w-16 rounded-md border border-border bg-background px-2 py-1 text-right text-sm tabular-nums outline-none focus:border-primary"
              />
              <span class="text-sm text-muted-foreground">%</span>
            </div>
            <div class="min-w-0 flex-1 max-w-xs">
              <Slider
                value={[draftWarn]}
                min={1}
                max={100}
                step={1}
                onValueChange={(v: number[]) => {
                  if (Array.isArray(v) && Number.isFinite(v[0])) setDraftWarn(v[0]);
                }}
                aria-label="Porcentagem de alerta"
              />
            </div>
          </div>
          <p class="mt-1 text-[11px] text-muted-foreground">
            Você recebe um aviso quando o consumo cruzar esse percentual do teto.
          </p>
        </div>

        <div class="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {saving ? "Salvando…" : "Salvar"}
          </button>
          {savedAt && (
            <span class="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="size-3.5" /> salvo agora
            </span>
          )}
        </div>
      </section>

      {/* Sobre BYOK + atalho para configurar a própria chave */}
      <ByokInlineCard />
    </div>
  );
}

// =====================================================================
// Atalho inline: quem está vendo AiSpendView (Controle de gastos) é
// exatamente quem se preocupa com R$ gasto na IA — esse mesmo público
// é o melhor candidato pra usar BYOK (sua própria chave). Em vez de
// mandar pro /ia (Assistente IA) que tem MUITA configuração, deixamos
// uma versão inline mais leve: modo (plataforma vs BYOK) + provedor.
// O botão "Configurar minha chave" leva pra tela completa quando o
// aluno quiser colar a API key.
// [[project-ai-spend-controls]] [[project-ai-byok-inline-cta]]
// =====================================================================

const PROVIDER_LABELS: Record<AiProvider, string> = {
  gemini: "Gemini",
  openai: "GPT / Codex",
  anthropic: "Claude",
  xai: "Grok",
  deepseek: "DeepSeek"
};

function ByokInlineCard() {
  const prefs = uiPrefs.value;
  const isBYOK = prefs.aiAccessMode === "byok";

  return (
    <section class="rounded-md border border-violet-500/30 bg-violet-500/5 p-4 text-sm">
      <div class="flex items-start gap-3">
        <CreditCard className="mt-0.5 size-5 shrink-0 text-violet-600" />
        <div class="min-w-0 flex-1 space-y-3">
          <div class="space-y-1">
            <h3 class="font-bold">E quando uso a minha própria chave?</h3>
            <p class="text-muted-foreground">
              Chamadas com a sua API key (BYOK){" "}
              <strong>não entram nesta conta</strong>. Elas continuam
              funcionando mesmo se o teto da plataforma for atingido. O custo
              dessas chamadas vai direto na sua fatura com o provedor (Gemini,
              OpenAI, DeepSeek, etc).
            </p>
            <p class="text-muted-foreground">
              Você ainda enxerga o consumo no histórico em "Consumo de
              tokens", mas separado por origem.
            </p>
          </div>

          {/* Escolha de modo: plataforma vs BYOK */}
          <div class="rounded-md border border-violet-500/20 bg-background/60 p-3">
            <div class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Quem paga as chamadas?
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAiAccessMode("platform")}
                aria-label="Usar a chave da plataforma"
                class={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  !isBYOK
                    ? "border-primary bg-primary text-white"
                    : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <Sparkles className="size-3.5" /> Plataforma
              </button>
              <button
                type="button"
                onClick={() => setAiAccessMode("byok")}
                aria-label="Usar minha própria API key"
                class={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isBYOK
                    ? "border-primary bg-primary text-white"
                    : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <Key className="size-3.5" /> Minha chave
              </button>
            </div>

            {isBYOK && (
              <div class="mt-3 space-y-3">
                <div>
                  <div class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Provedor
                  </div>
                  <div class="flex flex-wrap gap-1.5">
                    {(Object.keys(PROVIDER_LABELS) as AiProvider[]).map(
                      (provider) => {
                        const active = prefs.aiProvider === provider;
                        return (
                          <button
                            key={provider}
                            type="button"
                            onClick={() => setAiProvider(provider)}
                            aria-label={`Provedor ${PROVIDER_LABELS[provider]}`}
                            class={`inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                              active
                                ? "border-primary bg-primary/15 text-primary"
                                : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                            }`}
                          >
                            {PROVIDER_LABELS[provider]}
                          </button>
                        );
                      }
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setActiveNav("ia")}
                  class="inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
                >
                  <Settings2 className="size-3.5" />
                  Configurar minha API key e modelo
                  <ArrowRight className="size-3.5" />
                </button>
                <p class="text-[11px] text-muted-foreground">
                  A chave fica salva no seu dispositivo. Para escolher modelo
                  específico (Pro/Flash, GPT-4o/o1, etc.), abra a tela
                  completa.
                </p>
              </div>
            )}

            {!isBYOK && (
              <button
                type="button"
                onClick={() => {
                  setAiAccessMode("byok");
                }}
                class="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
              >
                Quero usar minha própria chave{" "}
                <ArrowRight className="size-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
